import { Inject, Injectable, Injector, OnDestroy } from "@angular/core";
import { BehaviorSubject, catchError, of, Subscription, tap } from "rxjs";
import environment from "../../environments/environment";
import { OktaAuthStateService, OKTA_AUTH } from "@okta/okta-angular";
import { OktaAuth } from "@okta/okta-auth-js";
import { MessageService } from "@app/services/message.service";
import { PlaceHolder } from "@app/interface/interfaces";
import { ApiService } from "@app/services/api.service";
import { MigrationStore } from "@app/services/migration.store";

@Injectable({
  providedIn: "root",
})
export class AuthService implements OnDestroy {
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  private usernameSubject = new BehaviorSubject<string | undefined>(undefined);
  private userPhotoUrlSubject = new BehaviorSubject<string | undefined>(
    undefined,
  );
  private hasAdminGroupSubject = new BehaviorSubject<boolean | undefined>(true);

  isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  username$ = this.usernameSubject.asObservable();
  userPhotoUrl$ = this.userPhotoUrlSubject.asObservable();
  hasAdminGroup$ = this.hasAdminGroupSubject.asObservable();

  private authSub?: Subscription;
  private sessionMonitoringSub?: Subscription;

  constructor(
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
    private authStateService: OktaAuthStateService,
    private injector: Injector,
    private apiService: ApiService,
    private migrationStore: MigrationStore,
  ) {
    // Start listening as soon as service is created
    this.initAuthStateListener();
    this.initSessionMonitoring();
  }

  public initAuthStateListener() {
    this.authSub = this.authStateService.authState$.subscribe((authState) => {
      this.handleAuthState(authState);
    });
  }

  public initPlaceHolders(): Promise<PlaceHolder[] | null> {
    return new Promise<PlaceHolder[] | null>((resolve) => {
      this.apiService
        .getPlaceHolders()
        .pipe(
          tap((placeHolders: PlaceHolder[]) => {
            this.migrationStore.setPlacehodler(placeHolders);
            resolve(placeHolders);
          }),
          catchError((error) => {
            resolve(null);
            return of(null);
          }),
        )
        .subscribe();
    });
  }

  /** Handles Okta authState updates */
  private async handleAuthState(authState: any) {
    const isAuthenticated = !!authState?.isAuthenticated;

    this.isAuthenticatedSubject.next(isAuthenticated);

    if (isAuthenticated) {
      this.initPlaceHolders();

      const userClaims = await this.oktaAuth.getUser();
      const username = userClaims["name"] || userClaims["preferred_username"];
      const userPhotoUrl =
        typeof userClaims["picture"] === "string"
          ? userClaims["picture"]
          : undefined;

      this.usernameSubject.next(username);
      this.userPhotoUrlSubject.next(userPhotoUrl);

      const groups = Array.isArray(userClaims["groups"])
        ? userClaims["groups"]
        : [userClaims["groups"]];
      this.hasAdminGroupSubject.next(
        groups.some(
          (group) =>
            typeof group === "string" &&
            group.toLowerCase().includes("app-erm-it-admin"),
        ),
      );
    } else {
      this.usernameSubject.next(undefined);
      this.userPhotoUrlSubject.next(undefined);
    }
  }

  login() {
    this.oktaAuth.signInWithRedirect();
  }

  async logout() {
    await this.oktaAuth.signOut({
      postLogoutRedirectUri: environment.okta.postLogoutRedirectUri,
    });
  }

  async getAccessToken(): Promise<string | undefined> {
    return await this.oktaAuth.getAccessToken();
  }

  private initSessionMonitoring() {
    // Use injector to avoid circular dependency
    // SessionTimeoutService depends on AuthService
    this.sessionMonitoringSub = this.isAuthenticated$.subscribe(
      (isAuthenticated) => {
        // Lazy load SessionTimeoutService to avoid circular dependency
        import("./session-timeout.service").then((module) => {
          const sessionTimeoutService = this.injector.get(
            module.SessionTimeoutService,
          );
          if (isAuthenticated) {
            sessionTimeoutService.startSessionMonitoring();
          } else {
            sessionTimeoutService.stopSessionMonitoring();
          }
        });
      },
    );
  }

  ngOnDestroy() {
    this.authSub?.unsubscribe();
    this.sessionMonitoringSub?.unsubscribe();
  }
}
